import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import { useEffect, useState } from 'react'

import { events, getSourceIDFromPathname } from '../../analytics/events'
import { getSubscriptionPreferences } from 'utils/subscriptions'
import { onChatStart, setChatUserDetails } from 'utils/zendesk'
import { track } from 'utils/analytics'
import { UserTerm } from 'types/internal'

import { useHasPastOrders } from 'hooks/orderHistory'
import { useOverlays } from 'contexts/overlays'
import { useUser } from 'contexts/user'
import AccountPage from './account/AccountPage'
import EditTermStatusSidebar from './EditTermStatusSidebar'
import Header from '../common/Header'
import MealPlanPreferencesModal from './MealPlanPreferencesModal'
import MenuPage from './menu/MenuPage'
import MyOrdersPage from './myOrders/MyOrdersPage'
import NotFoundPage from './NotFoundPage'
import UserLoader from 'components/common/UserLoader'
import ReferralModal from './ReferralModal'
import RatingPromptDialog, {
  DueRatingPrompt,
  useRatingPrompt,
} from './rateMeals/RatingPrompt'

const CorePages = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    onChatStart(() => {
      track(events.INITIATE_CHAT, {
        source_id: getSourceIDFromPathname(pathname),
      })
    })
  }, [pathname])

  return (
    <>
      <div>
        <Header />

        <UserLoader>
          <CoreLoaded />
        </UserLoader>
      </div>
    </>
  )
}

export default CorePages

type DialogData =
  | { type: 'editTermStatus'; userTerm: UserTerm }
  | { type: 'mealPlanPreferences' }
  | { ratingPrompt: DueRatingPrompt; type: 'ratingPrompt' }

const CoreLoaded = () => {
  const { user } = useUser()
  const [searchParams] = useSearchParams()

  const { isRatingDialogOpen, isReferralModalOpen, setIsReferralModalOpen } =
    useOverlays()

  const [dialogData, setDialogData] = useState<DialogData | null>(null)
  const [hasShownMealPlanPreferences, setHasShownMealPlanPreferences] =
    useState(false)

  const hasPastOrders = useHasPastOrders({ userID: user.id })
  // Wait for past orders so we don't show this immediately after checkout.
  const isMealPlanPreferencesDue =
    !hasShownMealPlanPreferences &&
    user.subscription.status === 'active' &&
    user.subscription.isDoNotReplace === null &&
    hasPastOrders

  const hasLoadedPastOrders = hasPastOrders !== undefined

  // Dialogs that open on their own wait for whatever the user has open, and
  // the meal plan preferences modal takes priority over the rating prompt.
  const canAutoOpenDialog =
    dialogData === null && !isReferralModalOpen && !isRatingDialogOpen

  useRatingPrompt({
    canOpenPrompt:
      canAutoOpenDialog && hasLoadedPastOrders && !isMealPlanPreferencesDue,
    onPromptDue: (ratingPrompt) => {
      setDialogData({ ratingPrompt, type: 'ratingPrompt' })
    },
  })

  useEffect(() => {
    if (user.info.email) {
      const email = user.info.email
      const name = user.info.name
      const phone = user.shippingAddresses[0]?.phone

      setChatUserDetails({ email, name, phone })
    }
  }, [user])

  useEffect(() => {
    if (isMealPlanPreferencesDue && canAutoOpenDialog) {
      setHasShownMealPlanPreferences(true)
      setDialogData({ type: 'mealPlanPreferences' })
    }
  }, [canAutoOpenDialog, isMealPlanPreferencesDue])

  useEffect(() => {
    const referralSearchParam = searchParams.get('referral')
    setIsReferralModalOpen(!!(referralSearchParam === 'true'))
  }, [searchParams, setIsReferralModalOpen])

  const onEditTermStatus = (userTerm: UserTerm) => {
    setDialogData({ type: 'editTermStatus', userTerm })
  }

  const closeDialog = () => {
    setDialogData(null)
  }

  return (
    <>
      <Routes>
        <Route
          element={
            <MenuPage isCurrentTerm onEditTermStatus={onEditTermStatus} />
          }
          path="menu/current"
        />
        <Route
          element={<MenuPage onEditTermStatus={onEditTermStatus} />}
          path="menu/:termID"
        />
        <Route
          element={<MyOrdersPage onEditTermStatus={onEditTermStatus} />}
          path="my-orders"
        />
        <Route
          element={<AccountPage onEditTermStatus={onEditTermStatus} />}
          path="account/*"
        />

        {/* Redirects */}
        <Route element={<Navigate replace to="/menu/current" />} path="/menu" />
        <Route
          element={<Navigate replace to="/menu/current" />}
          path="/account/meals"
        />
        <Route
          element={<Navigate replace to="/my-orders" />}
          path="/account/my-orders"
        />
        <Route
          element={
            <Navigate
              replace
              to={{
                pathname: '/account',
                search: 'referral=true',
              }}
            />
          }
          path="/account/referral"
        />
        <Route element={<Navigate to="/my-orders" />} path="/mapp/menu" />
        <Route element={<Navigate to="my-orders" />} path="/" />

        <Route element={<NotFoundPage />} path="*" />
      </Routes>

      {dialogData?.type === 'editTermStatus' && (
        <EditTermStatusSidebar
          onClose={closeDialog}
          selectedUserTerm={dialogData.userTerm}
        />
      )}

      {dialogData?.type === 'mealPlanPreferences' && (
        <MealPlanPreferencesModal
          closeModal={closeDialog}
          planPreferences={getSubscriptionPreferences({ user })}
        />
      )}

      {dialogData?.type === 'ratingPrompt' && (
        <RatingPromptDialog
          onClose={closeDialog}
          ratingPrompt={dialogData.ratingPrompt}
        />
      )}

      {isReferralModalOpen && (
        <ReferralModal
          onCloseModal={() => setIsReferralModalOpen(false)}
          user={user}
        />
      )}
    </>
  )
}
